import { useEffect, useMemo, useState, type FormEvent } from "react";
import { usePostHog } from "@posthog/react";
import { useSearchParams } from "react-router-dom";
import type { ToastState } from "../../ui/Toast/Toast";
import GuardianWorkspace from "../../guardian/GuardianWorkspace";
import { captureAdultGuardianRequestDecided, captureAdultGuardianRequestSent } from "../../../lib/analytics";
import { guardianApi } from "../../../lib/guardian";
import type { GuardianRequestDecision, GuardianRequestResponse } from "../../../types/guardianTypes";
import { useAuth } from "../../../hooks/useAuth";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { mapActiveTeens, mapIncomingTeenRequests, mapOutgoingTeenRequests } from "../../guardian/utils";

type TeenAttentionState = {
  label: string;
  description: string;
  count: number;
};

const deriveTeenAttention = (
  detail: Awaited<ReturnType<typeof guardianApi.getTeenDetail>>,
  viewerUserId: string | undefined,
): TeenAttentionState | null => {
  const pendingGuardianApprovals = detail.summary.pendingGuardianApprovals;
  const payoutNeedsAttention =
    detail.payoutProfile.payoutOwnerType === "GUARDIAN"
    && detail.payoutProfile.guardianUserId === viewerUserId
    && detail.payoutProfile.onboardingStatus !== "READY";

  if (pendingGuardianApprovals === 0 && !payoutNeedsAttention) {
    return null;
  }

  const parts: string[] = [];
  if (pendingGuardianApprovals > 0) {
    parts.push(
      `${pendingGuardianApprovals} gig request${pendingGuardianApprovals === 1 ? "" : "s"} waiting on approval`,
    );
  }
  if (payoutNeedsAttention) {
    parts.push("Stripe payout setup still needs to be finished");
  }

  return {
    label: "Needs attention",
    description: parts.join(" • "),
    count: pendingGuardianApprovals + (payoutNeedsAttention ? 1 : 0),
  };
};

const AdultGuardianWorkspace = () => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [incomingRequests, setIncomingRequests] = useState<GuardianRequestResponse[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<GuardianRequestResponse[]>([]);
  const [teenAttentionById, setTeenAttentionById] = useState<Record<string, TeenAttentionState | null>>({});
  const [targetEmail, setTargetEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const refresh = async () => {
    setIsLoading(true);
    setPageError(null);
    try {
      const [incoming, outgoing] = await Promise.all([guardianApi.listAdultIncoming(), guardianApi.listAdultOutgoing()]);
      setIncomingRequests(incoming);
      setOutgoingRequests(outgoing);

      const activeTeens = mapActiveTeens([...incoming, ...outgoing]);
      if (activeTeens.length === 0) {
        setTeenAttentionById({});
      } else {
        const teenDetails = await Promise.all(
          activeTeens.map(async (teen) => {
            try {
              const detail = await guardianApi.getTeenDetail(teen.id);
              return [teen.id, deriveTeenAttention(detail, user?.id)] as const;
            } catch {
              return [teen.id, null] as const;
            }
          }),
        );

        setTeenAttentionById(Object.fromEntries(teenDetails));
      }

      const guardianRequestTeenId = searchParams.get("guardianRequest");
      if (guardianRequestTeenId) {
        const matchingRequest = incoming.find((request) => request.teenUserId === guardianRequestTeenId && request.status === "PENDING");
        if (matchingRequest) {
          const teenName = [matchingRequest.teenUser?.firstName, matchingRequest.teenUser?.lastName].filter(Boolean).join(" ").trim();
          setToast({
            tone: "success",
            message: `${teenName || "That teen"} is waiting in your incoming guardian requests.`,
          });
        }

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("guardianRequest");
        setSearchParams(nextParams, { replace: true });
      }
    } catch (requestError) {
      setPageError(apiErrorToMessage(requestError, "Failed to load guardian data."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [user?.id]);

  const handleCreateRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!targetEmail.trim()) {
      return;
    }

    setSubmitting(true);
    setComposerError(null);
    try {
      const response = await guardianApi.createRequest({
        targetEmail: targetEmail.trim(),
        actingAsRole: "ADULT",
      });
      setTargetEmail("");
      await refresh();
      captureAdultGuardianRequestSent(posthog, {
        pending_signup: response.pendingSignup,
        source: "adult_guardian_workspace",
      });
      setToast({
        tone: "success",
        message: response.pendingSignup ? "Signup invite sent to that teen." : "Guardian request sent.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to send guardian request.");
      setComposerError(message);
      setToast({ tone: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (request: GuardianRequestResponse, decision: GuardianRequestDecision) => {
    const key = `${request.teenUserId}-${decision}`;
    setDecidingKey(key);
    setPageError(null);
    if (!request.teenUserId) {
      const message = "That guardian request is missing the teen account.";
      setPageError(message);
      setToast({ tone: "error", message });
      setDecidingKey(null);
      return;
    }
    try {
      await guardianApi.decideRequest(request.teenUserId, {
        decision,
        actingAsRole: "ADULT",
      });
      await refresh();
      captureAdultGuardianRequestDecided(posthog, {
        decision,
        source: "adult_guardian_workspace",
      });
      setToast({
        tone: "success",
        message: decision === "ACCEPTED" ? "Guardian request accepted." : "Guardian request declined.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to update guardian request.");
      setPageError(message);
      setToast({ tone: "error", message });
    } finally {
      setDecidingKey(null);
    }
  };

  const activeTeens = useMemo(
    () =>
      mapActiveTeens([...incomingRequests, ...outgoingRequests]).map((teen) => ({
        ...teen,
        attentionLabel: teenAttentionById[teen.id]?.label,
        attentionDescription: teenAttentionById[teen.id]?.description,
        attentionCount: teenAttentionById[teen.id]?.count,
        action: {
          label: "Open Teen Profile",
          variant: "outline" as const,
          href: `/adult/guardian/teens/${teen.id}`,
        },
      })),
    [incomingRequests, outgoingRequests, teenAttentionById],
  );

  const incomingSection = useMemo(
    () => ({
      title: "Incoming Requests",
      subtitle: "Teens asking you to be their guardian",
      emptyMessage: "No incoming guardian requests.",
      items: mapIncomingTeenRequests(incomingRequests).map((request) => ({
        ...request,
        actions: [
          {
            label: "Accept Request",
            variant: "primary" as const,
            pending: decidingKey === `${request.id}-ACCEPTED`,
            onClick: () => {
              const match = incomingRequests.find((item) => item.teenUserId === request.id);
              if (match) {
                void handleDecision(match, "ACCEPTED");
              }
            },
          },
          {
            label: "Decline Request",
            variant: "outline" as const,
            pending: decidingKey === `${request.id}-REJECTED`,
            onClick: () => {
              const match = incomingRequests.find((item) => item.teenUserId === request.id);
              if (match) {
                void handleDecision(match, "REJECTED");
              }
            },
          },
        ],
      })),
    }),
    [decidingKey, incomingRequests],
  );

  const outgoingSection = useMemo(
    () => ({
      title: "Sent Requests",
      subtitle: "Guardian requests you already sent to teens",
      emptyMessage: "You have not sent any guardian requests yet.",
      items: mapOutgoingTeenRequests(outgoingRequests),
    }),
    [outgoingRequests],
  );

  return (
    <GuardianWorkspace
      backButtonLabel="Back to Dashboard"
      backButtonFallbackTo="/dashboard"
      heroTitle="Manage your teen connections"
      heroDescription="Link Your Teens Account, respond to incoming guardian requests, and jump straight into each teen profile once the relationship is active."
      isLoading={isLoading}
      pageError={pageError}
      toast={toast}
      onDismissToast={() => setToast(null)}
      composer={{
        eyebrow: "Send A Request",
        title: "Link Your Teens Account",
        description: "Send a guardian connection request to a teen. Once accepted, you will be able to open their profile and approve gig participation when needed.",
        placeholder: "teen@example.com",
        submitLabel: "Send Request",
      }}
      targetEmail={targetEmail}
      composerError={composerError}
      submitting={submitting}
      onTargetEmailChange={setTargetEmail}
      onSubmit={handleCreateRequest}
      activeTitle="Current Teens"
      activeSubtitle="Your Connected Teens"
      activeEmptyMessage="No active teen relationships yet. Send a request to get started."
      activeItems={activeTeens}
      incomingSection={incomingSection}
      outgoingSection={outgoingSection}
    />
  );
};

export default AdultGuardianWorkspace;
